import { io, Socket } from 'socket.io-client';
import { App } from '@capacitor/app';
import { SERVER_URL, SOCKET_PATH } from '../config';
import type { ClientToServerEvents, ServerToClientEvents } from '@shared/events';
import type { RoomState } from '@shared/types';
import { LocalSinglePlayerSocket } from './LocalSinglePlayerSocket';
import { isNativeAppRuntime } from '../router';

type TypedSocket = Socket<ServerToClientEvents, ClientToServerEvents>;
type AppSocketEvent = keyof ServerToClientEvents | 'connect' | 'disconnect';
export type AppSocket = {
  connected: boolean;
  on(event: AppSocketEvent, listener: (...args: any[]) => void): AppSocket;
  once(event: AppSocketEvent, listener: (...args: any[]) => void): AppSocket;
  off(event: AppSocketEvent, listener?: (...args: any[]) => void): AppSocket;
  emit<E extends keyof ClientToServerEvents>(
    event: E,
    ...args: Parameters<ClientToServerEvents[E]>
  ): AppSocket;
  connect(): AppSocket;
  disconnect(): AppSocket;
};
type SocketMode = 'online' | 'local';

/**
 * SocketManager – Singleton für die Socket.IO-Verbindung zum Server.
 * Speichert Session-Daten für Reconnect.
 */
class SocketManager {
  private static instance: SocketManager;
  private onlineSocket: TypedSocket;
  private localSocket: LocalSinglePlayerSocket;
  private mode: SocketMode = 'online';
  private static readonly SESSION_PLAYER_ID_KEY = 'pickitup_playerId';
  private static readonly SESSION_ROOM_ID_KEY = 'pickitup_roomId';
  private static readonly SESSION_PLAYER_NAME_KEY = 'pickitup_playerName';
  private static readonly LOCAL_PLAYER_NAME_KEY = 'pickitup_playerName';
  private static readonly AUTH_TOKEN_KEY = 'pickitup_auth_token';
  private authToken = '';

  /** Aktive Session-Daten */
  public playerId = '';
  public roomId = '';
  public playerName = '';

  private constructor() {
    this.authToken = this.readAuthToken();
    const shouldAutoConnect = this.shouldAutoConnectOnlineSocket();
    this.onlineSocket = io(SERVER_URL, {
      path: SOCKET_PATH,
      autoConnect: shouldAutoConnect,
      reconnection: true,
      reconnectionAttempts: 10,
      reconnectionDelay: 1000,
      auth: { token: this.authToken },
    }) as TypedSocket;
    this.localSocket = new LocalSinglePlayerSocket();

    this.loadSession();
    this.setupReconnect();
  }

  static getInstance(): SocketManager {
    if (!SocketManager.instance) {
      SocketManager.instance = new SocketManager();
    }
    return SocketManager.instance;
  }

  getSocket(): AppSocket {
    return this.mode === 'local' ? this.localSocket : this.onlineSocket;
  }

  isLocalMode(): boolean {
    return this.mode === 'local';
  }

  setAuthToken(token: string): void {
    this.authToken = (token || '').trim();
    this.onlineSocket.auth = { token: this.authToken };
    try {
      if (this.authToken) localStorage.setItem(SocketManager.AUTH_TOKEN_KEY, this.authToken);
      else localStorage.removeItem(SocketManager.AUTH_TOKEN_KEY);
    } catch {
      // ignore
    }

    if (this.mode === 'online') {
      this.onlineSocket.disconnect();
      this.onlineSocket.connect();
    }
  }

  startLocalSinglePlayer(playerName: string): RoomState {
    const normalizedName = (playerName || '').trim().slice(0, 20);
    if (!normalizedName) {
      throw new Error('Name erforderlich');
    }

    this.setPlayerName(normalizedName);
    this.useLocalMode();
    const started = this.localSocket.startLocalSession(normalizedName);
    this.saveSession(started.playerId, started.roomId, normalizedName);
    return started.room;
  }

  /** Speichert Session-Daten für Reconnect */
  saveSession(playerId: string, roomId: string, playerName: string): void {
    this.playerId = playerId;
    this.roomId = roomId;
    this.setPlayerName(playerName);
    try {
      sessionStorage.setItem(SocketManager.SESSION_PLAYER_ID_KEY, playerId);
      sessionStorage.setItem(SocketManager.SESSION_ROOM_ID_KEY, roomId);
      if (isNativeAppRuntime()) {
        localStorage.setItem(SocketManager.SESSION_PLAYER_ID_KEY, playerId);
        localStorage.setItem(SocketManager.SESSION_ROOM_ID_KEY, roomId);
      }
    } catch {
      // sessionStorage nicht verfügbar
    }
  }

  /** Speichert den Spielernamen dauerhaft lokal für Browser/App/Desktop. */
  setPlayerName(playerName: string): void {
    const normalized = (playerName || '').trim().slice(0, 20);
    this.playerName = normalized;
    try {
      if (normalized) {
        localStorage.setItem(SocketManager.LOCAL_PLAYER_NAME_KEY, normalized);
        sessionStorage.setItem(SocketManager.SESSION_PLAYER_NAME_KEY, normalized);
      } else {
        localStorage.removeItem(SocketManager.LOCAL_PLAYER_NAME_KEY);
        sessionStorage.removeItem(SocketManager.SESSION_PLAYER_NAME_KEY);
      }
    } catch {
      // storage nicht verfügbar
    }
  }

  clearSession(): void {
    const wasLocal = this.mode === 'local';
    if (wasLocal) {
      this.localSocket.reset();
      this.mode = 'online';
    }

    this.playerId = '';
    this.roomId = '';
    try {
      sessionStorage.removeItem(SocketManager.SESSION_PLAYER_ID_KEY);
      sessionStorage.removeItem(SocketManager.SESSION_ROOM_ID_KEY);
      localStorage.removeItem(SocketManager.SESSION_PLAYER_ID_KEY);
      localStorage.removeItem(SocketManager.SESSION_ROOM_ID_KEY);
      // Name absichtlich behalten (für Vorausfüllung)
      if (this.playerName) {
        sessionStorage.setItem(SocketManager.SESSION_PLAYER_NAME_KEY, this.playerName);
      } else {
        sessionStorage.removeItem(SocketManager.SESSION_PLAYER_NAME_KEY);
      }
    } catch {
      // sessionStorage nicht verfügbar
    }

    if (wasLocal && !this.onlineSocket.connected) {
      this.onlineSocket.connect();
    }
  }

  hasSession(): boolean {
    return Boolean(this.playerId && this.roomId);
  }

  /**
   * Löscht gespeicherte Session und baut die Socket-Verbindung neu auf,
   * damit der Server den alten Raum-Status sauber beendet.
   */
  logoutAndResetConnection(): void {
    this.clearSession();
    this.onlineSocket.disconnect();
    this.onlineSocket.connect();
  }

  private loadSession(): void {
    try {
      this.playerId = sessionStorage.getItem(SocketManager.SESSION_PLAYER_ID_KEY) || '';
      this.roomId = sessionStorage.getItem(SocketManager.SESSION_ROOM_ID_KEY) || '';
      if (isNativeAppRuntime()) {
        this.playerId ||= localStorage.getItem(SocketManager.SESSION_PLAYER_ID_KEY) || '';
        this.roomId ||= localStorage.getItem(SocketManager.SESSION_ROOM_ID_KEY) || '';
      }
      this.playerName =
        sessionStorage.getItem(SocketManager.SESSION_PLAYER_NAME_KEY) ||
        localStorage.getItem(SocketManager.LOCAL_PLAYER_NAME_KEY) ||
        '';
    } catch {
      // sessionStorage nicht verfügbar
      try {
        this.playerName = localStorage.getItem(SocketManager.LOCAL_PLAYER_NAME_KEY) || '';
      } catch {
        // localStorage ebenfalls nicht verfügbar
      }
    }
  }

  /** Automatischer Reconnect-Versuch nach Verbindungsabbruch */
  private setupReconnect(): void {
    this.onlineSocket.on('connect', () => {
      if (this.mode !== 'online') return;
      if (this.playerId && this.roomId) {
        this.onlineSocket.emit('RECONNECT', {
          playerId: this.playerId,
          roomId: this.roomId,
        }, (res) => {
          if (!res.success) {
            console.warn('[Reconnect] Fehlgeschlagen:', res.error);
            // Session nur löschen, wenn der Raum definitiv weg ist oder die Session ungültig ist.
            // Transiente Fehler (Serverfehler, Auth-Token-Erneuerung) sollen die Session behalten.
            const definitiveErrors = [
              'Raum existiert nicht mehr',
              'Session ungültig',
              'Dieser Account spielt bereits auf einem anderen Client',
            ];
            if (definitiveErrors.some(msg => res.error?.includes(msg))) {
              this.clearSession();
              // Dispatch a local window event so UI can react to session loss
              try {
                window.dispatchEvent(new CustomEvent('pickitup:session-lost', { detail: { error: res.error } }));
              } catch {
                // ignore if no window
              }
            }
          } else {
            console.log('[Reconnect] Erfolgreich wiederverbunden');
          }
        });
      }
    });

    if (isNativeAppRuntime()) {
      App.addListener('appStateChange', ({ isActive }) => {
        if (isActive && this.mode === 'online') {
          if (this.onlineSocket.disconnected) this.onlineSocket.connect();
        }
      });
    }
  }

  private useLocalMode(): void {
    if (this.mode === 'local') return;
    this.mode = 'local';
    if (this.onlineSocket.connected) {
      this.onlineSocket.disconnect();
    }
    this.localSocket.connect();
  }

  private readAuthToken(): string {
    try {
      return localStorage.getItem(SocketManager.AUTH_TOKEN_KEY) || '';
    } catch {
      return '';
    }
  }

  private shouldAutoConnectOnlineSocket(): boolean {
    if (typeof window === 'undefined') return true;
    const path = (window.location.pathname || '').trim();
    if (!path) return true;
    // /pickitup/game (without room code) is dedicated offline singleplayer.
    return !/\/game\/?$/.test(path);
  }

  /**
   * Veranlasst den Spieler, den Raum explizit zu verlassen.
   * Sendet LEAVE_ROOM an den Server und löscht die Session.
   */
  leaveRoom(): void {
    if (this.mode === 'online' && this.onlineSocket.connected) {
      this.onlineSocket.emit('LEAVE_ROOM');
    }
    this.clearSession();
  }

}

export default SocketManager;
